/**
 * ServiceWorkerProvider Component
 * 
 * Client component that registers the service worker on mount
 * Enables offline MediaPipe caching for biometric verification
 * 
 * @author TotalAccess Development Team
 * @date November 3, 2025
 */

'use client';

import { useEffect } from 'react';
import { registerServiceWorker, checkMediaPipeCacheStatus, serviceWorkerWatchdog } from '@/lib/serviceWorkerRegistration';
import { messagingService } from '@/lib/messagingService';
import { isStandalonePwa } from '@/lib/pwaAppIdentity';
import { isCapacitorApp } from '@/lib/platformUtils';
import { useAuth } from '@/hooks/useAuth';
import { registerChunkErrorHandler } from '@/lib/chunkErrorHandler';
import { clearNativeCache } from '@/lib/nativeCacheBridge';

const SW_DEPLOYMENT_RECOVERY_RELOAD_KEY = 'ta-sw-deployment-recovery-reload-at';
const SW_DEPLOYMENT_RECOVERY_RELOAD_COOLDOWN_MS = 15000;
const SW_OFFLINE_WARMUP_SESSION_KEY = 'ta-sw-offline-warmup-complete-v1';

type StartupServiceWorkerStatus = 'idle' | 'registering' | 'registered' | 'ready' | 'error';

function reportStartupServiceWorkerStatus(status: StartupServiceWorkerStatus, detail?: Record<string, unknown>): void {
  if (typeof window === 'undefined') {
    return;
  }

  (window as Window & { __taServiceWorkerStatus?: StartupServiceWorkerStatus }).__taServiceWorkerStatus = status;
  window.dispatchEvent(new CustomEvent('ta-service-worker-status', {
    detail: {
      status,
      timestamp: Date.now(),
      ...detail,
    },
  }));
}

function shouldReloadForDeploymentRecovery(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const previousReloadAt = Number(sessionStorage.getItem(SW_DEPLOYMENT_RECOVERY_RELOAD_KEY) || '0');
  return !Number.isFinite(previousReloadAt) || Date.now() - previousReloadAt > SW_DEPLOYMENT_RECOVERY_RELOAD_COOLDOWN_MS;
}

function markDeploymentRecoveryReload(): void {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(SW_DEPLOYMENT_RECOVERY_RELOAD_KEY, String(Date.now()));
}

export default function ServiceWorkerProvider() {
  const { isAuthenticated } = useAuth();

  // Register global chunk error handler early — safety net for deployment mismatches.
  // Skip in dev when SW is disabled — HMR chunk failures are normal and the overlay
  // just adds noise / can interfere with development.
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_DISABLE_SW === 'true') {
      return;
    }
    registerChunkErrorHandler();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const shouldWarmOfflineShell = isAuthenticated && (isCapacitorApp() || isStandalonePwa());
    if (!shouldWarmOfflineShell) {
      return;
    }

    if (sessionStorage.getItem(SW_OFFLINE_WARMUP_SESSION_KEY) === 'done') {
      return;
    }

    const warmOfflineShell = async () => {
      try {
        const registration = await navigator.serviceWorker?.getRegistration('/');
        if (!registration?.active) {
          return;
        }

        const messageChannel = new MessageChannel();
        const resultPromise = new Promise<void>((resolve) => {
          messageChannel.port1.onmessage = () => resolve();
          window.setTimeout(() => resolve(), 120000);
        });

        registration.active.postMessage(
          { type: 'FORCE_CACHE_MODELS' },
          [messageChannel.port2],
        );

        await resultPromise;
        sessionStorage.setItem(SW_OFFLINE_WARMUP_SESSION_KEY, 'done');
      } catch (error) {
        console.warn('[ServiceWorkerProvider] Offline shell warm-up skipped:', error);
      }
    };

    void warmOfflineShell();
  }, [isAuthenticated]);

  useEffect(() => {
    // In dev with SW disabled, skip the message listener entirely.
    // An old/stale SW from a previous session can still post CACHE_CLEARED
    // messages even after unregistration, causing unwanted reloads during HMR.
    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_DISABLE_SW === 'true') {
      return;
    }

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      const { type, reason } = event.data || {};

      if (type === 'CACHE_CLEARED' && (reason === 'deployment_mismatch' || reason === 'sw_updated')) {
        if (!shouldReloadForDeploymentRecovery()) {
          console.warn('[ServiceWorkerProvider] Deployment recovery reload suppressed to avoid a loop');
          return;
        }

        console.warn('[ServiceWorkerProvider] Deployment mismatch detected, clearing native cache and reloading...');
        clearNativeCache();
        markDeploymentRecoveryReload();
        window.location.reload();
      }

      if (type === 'ALL_CACHES_CLEARED' && event.data?.success) {
        if (!shouldReloadForDeploymentRecovery()) {
          return;
        }

        console.warn('[ServiceWorkerProvider] All caches cleared, clearing native cache and reloading...');
        clearNativeCache();
        markDeploymentRecoveryReload();
        window.location.reload();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  useEffect(() => {
    // Register service worker on mount
    const initServiceWorker = async () => {
      try {
        reportStartupServiceWorkerStatus('registering');
        const registration = await registerServiceWorker();
        
        if (registration) {
          reportStartupServiceWorkerStatus('registered', {
            hasController: !!navigator.serviceWorker?.controller,
            scope: registration.scope,
          });

          try {
            await Promise.race([
              navigator.serviceWorker.ready,
              new Promise<null>((resolve) => setTimeout(() => resolve(null), 12000)),
            ]);
          } catch (_error) {
            // Best-effort readiness wait only.
          }

          reportStartupServiceWorkerStatus('ready', {
            hasController: !!navigator.serviceWorker?.controller,
            scope: registration.scope,
          });

          setTimeout(() => {
            void serviceWorkerWatchdog();
          }, 1000);

          // Service worker registered - check cache status silently
          setTimeout(async () => {
            await checkMediaPipeCacheStatus();
          }, 2000);
        } else {
          reportStartupServiceWorkerStatus('idle', {
            reason: 'registration_skipped_or_unavailable',
          });
        }
      } catch (error) {
        reportStartupServiceWorkerStatus('error', {
          message: error instanceof Error ? error.message : String(error),
        });
        // Only log errors
        console.error('[ServiceWorkerProvider] Failed to initialize service worker:', error);
      }
    };

    // Only run in browser
    if (typeof window !== 'undefined') {
      const handleControllerChange = () => {
        reportStartupServiceWorkerStatus('ready', {
          hasController: !!navigator.serviceWorker?.controller,
          reason: 'controllerchange',
        });
      };

      navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);
      initServiceWorker();

      return () => {
        navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
      };
    }
  }, []);

  useEffect(() => {
    const initMessaging = async () => {
      try {
        const isInstalledPwa = isStandalonePwa();
        const notificationsGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';

        // Bootstrap FCM for installed PWAs on every launch so granted permissions
        // turn into a registered browser token even when the user never revisits /install.
        if (!isInstalledPwa && !notificationsGranted) {
          return;
        }

        const supported = await messagingService.isSupported();
        if (!supported) {
          return;
        }

        await messagingService.initializeFCM();

        if (isAuthenticated) {
          await messagingService.ensureTokenRegistered();
        }
      } catch (error) {
        console.error('[ServiceWorkerProvider] Failed to initialize messaging service:', error);
      }
    };

    if (typeof window !== 'undefined') {
      void initMessaging();
    }
  }, [isAuthenticated]);

  // This component doesn't render anything
  return null;
}
